import { useState, useEffect } from "react";
import { X, Eye, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { EmailTemplate, UserEmailResponse } from "../api/useAdminEmails";

interface UserEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (
    template: EmailTemplate,
    dryRun: boolean,
    customBroadcastId?: string
  ) => void;
  isLoading: boolean;
  userName: string;
  userEmail: string;
  dryRunResult?: UserEmailResponse | null;
}

const EMAIL_TEMPLATE_OPTIONS = [
  {
    value: EmailTemplate.BOOKING_REMINDER,
    label: "T-48: Booking Reminder",
    description: "Remind user about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_REMINDER_51H,
    label: "T-51: Booking Reminder",
    description: "Remind user about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_REMINDER_72H,
    label: "T-72: Booking Reminder",
    description: "Remind user about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_DETAILS,
    label: "T-24: Booking Details",
    description: "Send complete booking information and table details",
  },
  {
    value: EmailTemplate.BOOKING_ABOUT_TO_HAPPEN,
    label: "T-1: Booking About to Happen",
    description: "Notify user that their booking is about to happen",
  },
  {
    value: EmailTemplate.POST_BOOKING_SURVEY,
    label: "T+16: Post-Booking Survey",
    description: "Request feedback after the event",
  },
  {
    value: EmailTemplate.BOOKING_UPDATED,
    label: "Booking Updated",
    description: "Notify user their booking details have changed",
  },
  {
    value: EmailTemplate.UNABLE_TO_BE_SEATED,
    label: "Unable To Be Seated",
    description: "Notify user we were unable to seat them at a table",
  },
];

export function UserEmailModal({
  isOpen,
  onClose,
  onSend,
  isLoading,
  userName,
  userEmail,
  dryRunResult,
}: UserEmailModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>(
    EmailTemplate.BOOKING_DETAILS
  );
  const [customBroadcastId, setCustomBroadcastId] = useState<string>("");

  useEffect(() => {
    if (!isOpen) {
      setCustomBroadcastId("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedBroadcastId = customBroadcastId.trim();
  const broadcastIdOverride =
    trimmedBroadcastId.length > 0 ? trimmedBroadcastId : undefined;

  const handleDryRun = () => {
    onSend(selectedTemplate, true, broadcastIdOverride);
  };

  const handleSend = () => {
    onSend(selectedTemplate, false, broadcastIdOverride);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <div>
            <h2 className="text-lg font-bold">Send Email to User</h2>
            <p className="text-base-content/60 text-xs">
              {userName} &lt;{userEmail}&gt;
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-xs btn-circle w-8 h-8 p-1"
            disabled={isLoading}
          >
            <X width={32} height={32} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="label py-1">
              <span className="label-text text-xs font-semibold">
                Email Template
              </span>
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) =>
                setSelectedTemplate(e.target.value as EmailTemplate)
              }
              className="select select-bordered select-sm w-full"
            >
              {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-xs font-semibold">
                Custom Broadcast ID
              </span>
              <span className="label-text-alt text-[10px] text-base-content/60">
                Optional — overrides the template's default
              </span>
            </label>
            <input
              type="text"
              value={customBroadcastId}
              onChange={(e) => setCustomBroadcastId(e.target.value)}
              placeholder="e.g. 65"
              className="input input-bordered input-sm w-full"
              inputMode="numeric"
            />
          </div>

          {dryRunResult && (
            <details
              className="collapse collapse-arrow bg-base-200 border border-base-300"
              open
            >
              <summary className="collapse-title min-h-0 py-2 px-3 cursor-pointer font-semibold text-sm flex items-center gap-2">
                <span>Dry Run Result</span>
                {dryRunResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-success" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-error" />
                )}
              </summary>
              <div className="collapse-content px-3 pb-3 space-y-3">
                <div className="text-xs">
                  <p className="text-base-content/60">
                    {dryRunResult.recipients.length} recipient(s)
                  </p>
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {dryRunResult.recipients.map((r) => (
                    <div
                      key={r.userId}
                      className="flex items-center justify-between text-[11px] p-1 bg-base-100 rounded"
                    >
                      <span className="truncate">
                        {r.firstName} {r.lastName} &lt;{r.email}&gt;
                      </span>
                      {dryRunResult.success ? (
                        <CheckCircle2 className="w-3 h-3 text-success shrink-0 ml-2" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-error shrink-0 ml-2" />
                      )}
                    </div>
                  ))}
                </div>

                {dryRunResult.error && (
                  <p className="text-xs text-error">{dryRunResult.error}</p>
                )}

                {dryRunResult.request && (
                  <details className="collapse collapse-arrow bg-base-100 border border-base-300">
                    <summary className="collapse-title min-h-0 py-1.5 px-2 cursor-pointer font-medium text-[11px]">
                      View Customer.io API Request
                    </summary>
                    <div className="collapse-content px-2 pb-2">
                      <pre className="bg-base-300 p-2 rounded overflow-auto max-h-48 text-[9px] leading-tight">
                        {JSON.stringify(dryRunResult.request, null, 2)}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-base-300">
          <button
            onClick={handleDryRun}
            className="btn btn-secondary btn-sm gap-1"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Eye className="w-3 h-3" />
            )}
            Dry Run
          </button>
          <button
            onClick={handleSend}
            className="btn btn-primary btn-sm gap-1"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            Send Email
          </button>
        </div>
      </div>
    </div>
  );
}
